'use client';
import { useState, useEffect, useCallback } from 'react';
import {
  resolveStoreId,
  clearStoreIdCache,
  setImpersonatedStoreId,
  getImpersonatedStoreId,
  getCachedStoreId,
} from '@/lib/services/storeIdResolver';

export { resolveStoreId, clearStoreIdCache, setImpersonatedStoreId };

export function useStoreId(): {
  storeId: string | null;
  loading: boolean;
  error: string | null;
} {
  const [storeId, setStoreId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const resolve = useCallback(async () => {
    const impersonated = getImpersonatedStoreId();
    if (impersonated !== null) {
      setStoreId(impersonated);
      setLoading(false);
      return;
    }

    const cached = getCachedStoreId();
    if (cached !== null) {
      setStoreId(cached);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const id = await resolveStoreId();
      setStoreId(id);
      if (id === null) {
        setError('No store assigned to this user.');
      }
    } catch (err: any) {
      setError(err?.message ?? 'Failed to resolve store.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    resolve();
  }, [resolve]);

  return { storeId, loading, error };
}
